import { checkAdsHide } from "../wniutil/auth.js";
import {
	compareVersion,
	getSmartPlatform,
	getSmartWebview,
	getVersion,
	isValidValue,
	readParams,
	snakeToCamel,
	isVisibleElement,
} from "../wniutil/common.js";
import { loadAdSenseJS, loadApsTagJS, loadGptJS, loadPwtJS } from "./load.js";
import { setPpid } from "./ppid.js";

export class WNIAds {
	constructor(opt) {
		this.prebidFailSafeTimeout = 15e2;
		this.apstagFailSafeTimeout = 1e3;

		this.initialized = false;
		this.requiredBids = {
			prebid: false,
			amazon: false,
			google: true,
		};
		this.readyBids = {
			prebid: false,
			amazon: false,
			google: false,
		};
		this.options = {};
		this.slots = {};
		this.stickyAd = null;
		this.stickyClosed = false;
		this.isReset = false;
		this.useAds = false;
		this.apstagSlots = [];
		this.smartWebview;
		this.smartPlatform;

		if (opt?.loadPrebids) {
			loadPwtJS();
		}

		this.#compatibility(opt);

		if (opt?.gptConf) {
			this.init(opt);
		}
	}

	// 流れ
	// クラスの初期化
	// 広告のサイズ設定などを指定 → init
	// 有料無料判定 → checkAdsHide
	//   有料の場合、広告を削除 → removeAds
	//   無料の場合、広告を表示 → initAds
	//     Google広告の設定呼び出し → initGpt
	//     Amazon広告の呼び出し → initApstag
	//     Prebidの呼び出し → initPrebid
	//     Google広告の取得 → requestAdServer

	/**
	 * 広告設定を初期化
	 * @param {*} opt
	 */
	init = (opt = {}) => {
		this.#setup(opt);

		if (this.initialized) {
			this.reset();
		} else {
			this.smartPlatform = getSmartPlatform();
			this.smartWebview = getSmartWebview();

			this.initialized = true;
			window.googletag = window.googletag || {};
			window.googletag.cmd = window.googletag.cmd || [];
			window.PWT = window.PWT || {};
			this.slots = {};
		}
		this.#execute(opt);
	};

	#compatibility = (opt) => {
		// compatibility
		if (opt && "use_default" in opt) {
			opt.useDefault = opt.use_default;
			console.warn("use_default is deprecated. Please use useDefault instead.");
		}
		if (opt?.use_prebids) {
			opt.usePrebids = opt.use_prebids;
			console.warn("use_prebids is deprecated. Please use usePrebids instead.");
		}
		if (opt?.gpt_sticky_conf) {
			this.stickyType = this.#getStickyType(opt.sticky);
			const stickyConf = opt.gpt_sticky_conf[this.stickyType];
			if (stickyConf) {
				stickyConf.sticky = true;
				stickyConf.targetPlatform = "sp";
				opt.gpt_conf = opt.gpt_conf || [];
				opt.gpt_conf.push(stickyConf);
			}
		}
		if (opt?.gpt_conf) {
			opt.gptConf = opt.gpt_conf;
			for (const i in opt.gptConf) {
				// snake case to camel case automatically
				for (const key in opt.gptConf[i]) {
					const newKey = snakeToCamel(key);
					if (newKey !== key) {
						opt.gptConf[i][newKey] = opt.gptConf[i][key];
						delete opt.gptConf[i][key];
					}
				}
			}
			console.warn("gpt_conf is deprecated. Please use gptConf instead.");
		}
		// compatibility end

	}

	#setup = (opt = {}) => {
		if(Object.keys(opt).length >= 0) {
			this.#compatibility(opt);
			this.options = opt;
		}
		if (!("usePrebids" in this.options) || this.options.usePrebids === true) {
			this.requiredBids.prebid = true;
		}
	};

	#isReadyAdServer = () => {
		if (this.requiredBids.amazon && !this.readyBids.amazon) return false;
		if (this.requiredBids.prebid && !this.readyBids.prebid) return false;
		return true;
	};

	/**
	 * Google広告にリクエストを出す関数
	 * @param {Boolean} force 強制的にGoogle広告のリクエストを出すかどうか
	 * @returns
	 */
	#requestAdServer = (force) => {
		if (!force && !this.#isReadyAdServer()) return;
		if (this.readyBids.google) return;
		this.readyBids.google = true;
		window.googletag.cmd.push(() => {
			const slots = googletag.pubads().getSlots();
			googletag.pubads().refresh(slots);
		});
	};

	#initPrebid = () => {
		if (!this.requiredBids.prebid) return;

		loadPwtJS();

		window.googletag.cmd.push(() => {
			if (typeof window.PWT.requestBids === "function") {
				window.PWT.requestBids(
					window.PWT.generateConfForGPT(googletag.pubads().getSlots()),
					(adUnitsArray) => {
						window.PWT.addKeyValuePairsToGPTSlots(adUnitsArray);
						window.PWT.ow_BidsReceived = this.readyBids.prebid = true;
						this.#requestAdServer();
					},
				);
			}
			setTimeout(() => {
				if (this.readyBids.google === false) {
					this.#requestAdServer(true); // TODO
				}
			}, this.prebidFailSafeTimeout);
		});
	};

	/**
	 * Google広告に対応したTAMを挿入する
	 * @function
	 *     set_dfp より後にこの関数を実行すること
	 */
	#initApstag = () => {
		this.requiredBids.amazon = this.apstagSlots.length > 0;
		if (!this.requiredBids.amazon) return;

		loadApsTagJS();

		window.apstag.init({
			pubID: "3792",
			adServer: "googletag",
			bidTimeout: this.apstagFailSafeTimeout,
		}); // request the bids for the four googletag slots
		window.apstag.fetchBids(
			{
				slots: this.apstagSlots,
				timeout: this.apstagFailSafeTimeout,
			},
			(bids) => {
				// set apstag bids, then trigger the first request to DFP
				window.googletag.cmd.push(() => {
					this.readyBids.amazon = true;
					window.apstag.setDisplayBids();
					if (this.requiredBids.prebid) {
						window.PWT.TAM_BidsReceived = true;
					}
					this.#requestAdServer();
				});
			},
		);
	};

	#initAdSense = () => {
		// data-ad-client が設定されている div があれば、loadAdSenseJS を呼び出す
		const adSenseSlots = document.querySelectorAll("ins[data-ad-client]");
		const visibleAdSenseSlots = Array.from(adSenseSlots).filter((slot) => {
			return isVisibleElement(slot);
		});
		if (visibleAdSenseSlots.length > 0) {
			loadAdSenseJS();
		}
	};

	#execute = async (opt = {}) => {
		if(Object.keys(opt).length >= 0) {
			this.#compatibility(opt);
			this.options = opt;
		}
		if (this.options.useDefault !== false) {
			const isAdsHide = await checkAdsHide(this.options.checkAdsHideOption);
			if (isAdsHide) {
				this.useAds = false;
				this.options?.initCallback?.({ isAdsHide: true });
				this.hideAdsAll();
			} else {
				this.useAds = true;
				this.options?.initCallback?.({ isAdsHide: false });
				this.initAds();
			}
		}
	};

	reset = () => {
		this.isReset = true;
		this.readyBids = {
			prebid: false,
			amazon: false,
			google: false,
		};
		this.requiredBids = {
			prebid: true,
			amazon: true,
			google: true,
		};
	};

	refresh = () => {
		if (this.useAds) {
			this.reset();
			this.initAds();
		}
	};

	#initGpt = () => {
		loadGptJS();

		const lazyloadOptions = {
			fetchMarginPercent: this.smartPlatform ? 100 : 0,
			renderMarginPercent: this.smartPlatform ? 50 : 0,
		};
		if (this.options.lazyload) {
			for (const key of Object.keys(this.options.lazyload)) {
				lazyloadOptions[key] = this.options.lazyload[key];
			}
		}
		window.googletag.cmd.push(() => {
			window.googletag.pubads().collapseEmptyDivs();
			if (this.options.enableSingleRequest) {
				window.googletag.pubads().enableSingleRequest();
			} else {
				window.googletag.pubads().disableInitialLoad(); // setTargeting と enablelazyLoad は必ず併用する
				window.googletag.pubads().enableLazyLoad(lazyloadOptions);
			}
			window.googletag.enableServices();
		});

		if (this.options.gptConf) {
			for (const key in this.options.gptConf) {
				if (this.options.gptConf[key].sticky && this.stickyClosed) continue;
				this.#setDfp(this.options.gptConf[key]);
			}
		}
	};

	initAds = async () => {
		if (this.options.noPpid !== true) setPpid();
		this.#initGpt();
		this.#initPrebid();
		this.#initApstag();
		this.#initAdSense()
	};

	init_ads = () => {
		this.initAds();
		console.warn("init_ads is deprecated. Please use initAds instead.");
	};

	/**
	 * 設定に応じたGoogle広告を表示する
	 * TAM 用の配列にも情報を入れる
	 * @param {Object} gpt
	 * @param {String} opt.unit_code
	 * @param {String} opt.slot_id
	 * @param {Array}  opt.size
	 * @param {Array}  [opt.mapping]
	 * @param {Object} [opt.targeting]
	 * @param {Boolean} [opt.apstag]
	 * @function
	 */
	#setDfp = (gptConf) => {
		if (!gptConf) return;
		if (!document.getElementById(gptConf.slotId)) return;
		if (gptConf.targetPlatform === "pc" && this.smartPlatform !== "") {
			this.removeAds(gptConf);
			return;
		}
		if (gptConf.targetPlatform === "sp" && this.smartPlatform === "") {
			this.removeAds(gptConf);
			return;
		}
		if (
			gptConf.sticky &&
			this.smartWebview === "webview" &&
			gptConf.disableApp === true
		) {
			this.removeAds(gptConf);
			return;
		}

		if (typeof gptConf === "object" && isValidValue(gptConf.unitCode)) {
			window.googletag.cmd.push(() => {
				this.showSlot(gptConf.slotId);
				const gt =
					this.slots[gptConf.slotId] ||
					window.googletag.defineSlot(
						gptConf.unitCode,
						gptConf.size,
						gptConf.slotId,
					);
				if (gptConf.mapping) {
					const mapping = window.googletag.sizeMapping();
					for (let i = 0; i < gptConf.mapping.length; i++) {
						mapping.addSize(
							gptConf.mapping[i].displaySize,
							gptConf.mapping[i].adSize,
						);
					}
					const builtMapping = mapping.build();
					gt.defineSizeMapping(builtMapping);
				}
				if (gptConf.targeting) {
					for (const key in gptConf.targeting) {
						gt.setTargeting(key, gptConf.targeting[key]);
					}
				}
				if (gptConf.sticky) {
					// 必ず表示される要素
					gt.setCollapseEmptyDiv(true);
					// 入れ子構造になるため、内部に要素が追加されたときに親要素を表示するようにする
					const stickyTarget = document.getElementById(gptConf.slotId);
					this.stickyAd = stickyTarget.parentNode;
					const showStickyAd = (records) => {
						let hasChild = false;
						// カラの要素が返ってきた場合は表示しない
						for (let i = 0; i < records.length; i++) {
							if (records[i].target.getAttribute("id") !== gptConf.slotId) {
								// 子は無視。孫を確認
								hasChild = records[i].target.hasChildNodes();
								if (hasChild) break;
							}
						}
						if (hasChild) {
							this.stickyAd.style.display = "block";
							observer.disconnect();
						}
					};
					const observer = new MutationObserver(showStickyAd);
					observer.observe(stickyTarget, { childList: true, subtree: true });
				}
				gt.addService(window.googletag.pubads());
				this.slots[gptConf.slotId] = gt;
			});

			if (gptConf.apstag) {
				const apstagSizes = gptConf.size.filter((a) => {
					return a !== "fluid";
				});
				if (apstagSizes.length > 0) {
					this.apstagSlots.push({
						slotID: gptConf.slotId,
						slotName: gptConf.unitCode,
						sizes: apstagSizes,
					});
				}
			}
		} else {
			this.hideSlot(gptConf.slotId);
		}
	};

	/**
	 * 指定したIDのDOMを非表示にする
	 * sticky adは、入れ子構造になっているため、親要素も消す
	 * @param {String} slotId
	 * @function
	 */
	hideSlot = (slotId) => {
		if (document.getElementById(slotId))
			document.getElementById(slotId).style.display = "none";
		if (this.stickyAd && this.stickyAd.querySelector("div").id === slotId)
			this.stickyAd.style.display = "none";
	};

	/**
	 * 指定したIDのDOMを表示する
	 * @param {String} slotId
	 * @param {String} [display_style='block']
	 * @function
	 */
	showSlot = (slotId, displayStyle = "block") => {
		if (document.getElementById(slotId)) {
			document.getElementById(slotId).style.display = displayStyle;
		}
	};

	/**
	 * sticky-ad の種類を特定する（キャリア判別）
	 * @function
	 */
	#getStickyType = ({ disableApp = false, disablePC = true, ua } = {}) => {
		const params = readParams();
		const version = getVersion();
		/* import from launch.js */
		if (this.smartWebview === "webview") {
			if (this.smartPlatform === "iOS" && params.mywx !== 1) {
				if (isValidValue(version) && compareVersion(version, "5.31.0") >= 0) {
					return "default";
				}
				return disableApp === true ? "" : "app";
			}
			if (this.smartPlatform === "android" && params.mywx !== 1) {
				return "default";
			}
			return disableApp === true ? "" : "app";
		}
		if (this.smartPlatform === "") {
			return disablePC === true ? "" : "pc";
		}

		return "default";
	};

	/**
	 * sticky-ad を閉じる
	 * @function
	 */
	closeSticky = () => {
		this.stickyClosed = true;
		if (this.stickyAd.classList.contains("pc")) {
			const onAnimationEndPC = (e) => {
				this.stickyAd.removeEventListener("animationend", onAnimationEndPC);
				this.stickyAd.parentNode.removeChild(this.stickyAd);
			};
			this.stickyAd.classList.add("hide");
			this.stickyAd.addEventListener("animationend", onAnimationEndPC);
		} else {
			const onTransitionEndSp = (e) => {
				if (this.stickyAd.style.opacity < 0.1) {
					this.stickyAd.style.display = "none";
					this.stickyAd.style.transition = "none";
					this.stickyAd.removeEventListener("transitionend", onTransitionEndSp);
					this.stickyAd.parentNode.removeChild(this.stickyAd);
				}
			};
			if (this.stickyAd) {
				this.stickyAd.style.transition = "all 0.4s ease-in-out";
				this.stickyAd.style.transform = "translateY(50px)";
				this.stickyAd.style.opacity = 0;
				this.stickyAd.addEventListener("transitionend", onTransitionEndSp);
			}
		}
	};

	close_sticky = () => {
		this.closeSticky();
		console.warn("close_sticky is deprecated. Please use closeSticky instead.");
	};

	/**
	 * ad を消す
	 * @param {Object} gptConf 広告枠の設定（gpt_conf に設定されているものの1枠）
	 * @function
	 */
	removeAds = (gptConf) => {
		const target =
			gptConf.sticky || gptConf.hasParent
				? document.getElementById(gptConf.slotId)?.parentNode
				: document.getElementById(gptConf.slotId);

		if (target) {
			target.style.display = "none";
		}
	};

	/**
	 * AdSense広告を非表示にする
	 * @function
	 */ 
	hideAdSenseAll = () => {
		const adSenseSlots = document.querySelectorAll("ins[data-ad-client]");
		for (const adSenseSlot of adSenseSlots) {
			adSenseSlot.style.display = "none";
		}
	};

	/**
	 * 広告枠を全て非表示にする
	 * @function
	 */
	hideAdsAll = () => {
		for (const gptConf of this.options.gptConf) {
			this.removeAds(gptConf);
		}
		this.hideAdSenseAll();
	};
}
